import { Navigate } from 'react-router-dom';
import React, { ReactNode, useEffect } from 'react';

// Sentry
import * as Sentry from '@sentry/react';

// Hooks
import { useAuth } from '../hooks/useAuth';

// Types
import { Role } from '../types/auth';

// Utils
import { getDefaultRouteForRole } from './utils';
import { showErrorToast } from '../utils/toasts';
import i18n from '../i18n';

/**
 * Guard de ruta por rol.
 *
 * Ante un rol no autorizado NO se manda al usuario a `/login`. Su sesión es
 * perfectamente válida y decirle "identifícate" es mentira. Hasta 2026-08-07 se
 * hacía así, y el rebote —`/login` → `AuthLayout` ve la sesión → portada del
 * rol— desmontaba la aplicación entera EN SILENCIO: sin mensaje, sin consola y
 * sin telemetría.
 *
 * Eso convertía cualquier lista de roles incompleta en pérdida de trabajo. El
 * caso real: un capataz pulsaba "Continuar" en el wizard de actuación, la
 * propia aplicación lo navegaba al paso de partidas —prohibido para él— y
 * aparecía en la portada con la actuación a medias abandonada en `partial`. El
 * usuario lo describió como "parece que refresca", que es exactamente lo que se
 * ve cuando esto ocurre, y no había forma de diagnosticarlo desde la app.
 *
 * Ahora: portada del rol + aviso visible + rastro en Sentry, para que el
 * próximo olvido se detecte sin depender de que alguien lo cuente.
 *
 * OJO: se comprueba el ARRAY `roles`, no `rolActive`. Un usuario con varios
 * roles pasa el guard si CUALQUIERA de ellos está permitido.
 */
export function RolesAuthRoute({
  children,
  roles,
}: {
  children: ReactNode;
  roles: Array<Role>;
}) {
  const auth = useAuth();
  const userRoles: Array<Role> | undefined = auth?.user?.user?.roles as
    | Array<Role>
    | undefined;
  const activeRole = auth?.user?.user?.rolActive as Role | undefined;

  const canAccess = userRoles?.some((userRole) => roles.includes(userRole));

  useEffect(() => {
    if (canAccess) return;

    showErrorToast(i18n.t('errors:permissionDenied'));
    Sentry.captureMessage('RolesAuthRoute: acceso denegado', {
      level: 'warning',
      extra: {
        path: window.location.pathname,
        rolActive: activeRole ?? null,
        userRoles: userRoles ?? null,
        allowedRoles: roles,
      },
    });
  }, [canAccess]);

  if (canAccess) return <>{children}</>;

  // `getDefaultRouteForRole` ya devuelve `/login` como último recurso si el rol
  // no tiene ninguna ruta permitida, así que una sesión rota de verdad sigue
  // acabando en la pantalla de sesión.
  return <Navigate to={getDefaultRouteForRole(activeRole as Role)} replace />;
}
